import { Link, useNavigate, useParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { ChordDiagram } from '@/components/ChordDiagram';
import { Button, Card, SectionTitle } from '@/components/ui';
import { getChord } from '@/data/chords';
import { expectedNotes, indexToStringNumber, PITCH_CLASS_NAMES } from '@/data/tuning';
import { t } from '@/i18n';
import './ChordDetailPage.css';

/** One chord in full: big diagram, what each string does, and a way to practise it. */
export function ChordDetailPage() {
  const { chordId } = useParams<{ chordId: string }>();
  const navigate = useNavigate();
  const chord = chordId ? getChord(chordId) : undefined;

  if (!chord) {
    return (
      <AppShell title={t('chords.title')} showBack>
        <Card>
          <p className="library__empty-title">{t('chords.empty')}</p>
          <p className="muted">{t('chords.emptyHint')}</p>
        </Card>
      </AppShell>
    );
  }

  const sounding = expectedNotes(chord.frets);
  const easier = chord.beginnerVariantOf ? undefined : findEasier(chord.id);

  return (
    <AppShell title={chord.name} showBack>
      <Card section className="detail__hero">
        <ChordDiagram chord={chord} size={250} className="chord-diagram--accent" />
        <p className="detail__name-ru">{chord.nameRu}</p>
        <p className="detail__difficulty">
          {t('chords.difficulty')}: {'●'.repeat(chord.difficulty)}
          <span className="detail__difficulty-rest">{'●'.repeat(5 - chord.difficulty)}</span>
        </p>
      </Card>

      {chord.tipRu && (
        <Card className="detail__tip">
          <p>{chord.tipRu}</p>
        </Card>
      )}

      <section className="page-section detail__strings-section">
        <SectionTitle>{t('chords.strings')}</SectionTitle>
        <Card padded={false}>
          <ul className="detail__strings">
            {chord.frets.map((fret, index) => {
              const stringNumber = indexToStringNumber(index);
              const note = sounding.find((item) => item.string === stringNumber);
              return (
                <li key={stringNumber} className="detail__string">
                  <span className="detail__string-number">{stringNumber}</span>
                  <span className="detail__string-what">
                    {fret === null
                      ? t('chords.notPlayed')
                      : fret === 0
                        ? t('chords.open')
                        : t('chords.fret', { fret })}
                  </span>
                  <span className="detail__string-note">
                    {note ? PITCH_CLASS_NAMES[note.pitchClass] : '—'}
                  </span>
                  <span className="detail__string-finger muted">
                    {chord.fingers[index] > 0 ? t('chords.finger', { finger: chord.fingers[index] }) : ''}
                  </span>
                </li>
              );
            })}
          </ul>
        </Card>
        {chord.barre && (
          <p className="detail__barre">{t('chords.barre', { fret: chord.barre.fret })}</p>
        )}
      </section>

      {easier && (
        <Link to={`/chords/${easier}`} className="detail__easier">
          {t('chords.easierVariant', { chord: getChord(easier)?.nameRu ?? easier })}
        </Link>
      )}

      <Button
        large
        block
        onClick={() =>
          navigate('/training/clean-chords', {
            state: {
              recommendation: {
                mode: 'cleanChord',
                chordIds: [chord.id],
                bpm: 0,
                durationMin: 3,
                reason: 'lowClarity',
                explanationRu: '',
              },
            },
          })
        }
      >
        <Play size={20} fill="currentColor" aria-hidden="true" />
        {t('chords.practice')}
      </Button>
    </AppShell>
  );
}

/** The easier voicing registered for this chord, if the library has one. */
function findEasier(chordId: string): string | undefined {
  const easier = ['F_easy'].find((id) => getChord(id)?.beginnerVariantOf === chordId);
  return easier;
}
